import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Content from '../../Content';
import {
  findSearchProfiles,
  findSearchClearanceLevels,
  findSearchUsers,
  updateSearchUser,
} from '../SecFunctions';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { XGrid } from '@material-ui/x-grid';
import CheckIcon from '@material-ui/icons/Check';
import { formatUTCdate } from 'utils/functions';
import Button from '@material-ui/core/Button';
import SaveIcon from '@material-ui/icons/Save';
import { handleError } from 'reducers/ErrorReducer';
import { notify } from 'reducers/NotifierReducer';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { showAddSecUser, showRemoveSecUser } from 'reducers/DialogsReducer';
import Fab from '@material-ui/core/Fab';
import Tooltip from 'components/Tooltip';
import { Box, Grid } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  autoComplete: {
    marginTop: '15px',
  },
  list: {
    width: '100%',
    height: 600,
  },
  saveBtn: {
    textAlign: 'right',
    marginBottom: 15,
  },
  textField: { marginTop: '15px' },
}));

const bool = params => (params.value ? <CheckIcon /> : <span></span>);

const columns = [
  { field: 'Username', headerName: 'Username', width: 150 },
  { field: 'FullName', headerName: 'Fullname', width: 200 },
  { field: 'Profile', headerName: 'Profile', width: 120 },
  { field: 'IsDisabled', headerName: 'Disabled', width: 120, format: 'bool', renderCell: bool },
  { field: 'IsLockedOut', headerName: 'Locked Out', width: 120, format: 'bool', renderCell: bool },
  {
    field: 'ActivityDateTime',
    headerName: 'Activity Date Time',
    width: 150,
    valueFormatter: params => formatUTCdate(params.value),
    format: 'date',
  },
  { field: 'Activity', headerName: 'Activity', width: 120 },
  { field: 'PersonID', headerName: 'PersonID', width: 120 },
  { field: 'ClearanceLevel', headerName: 'Clearance Level', width: 150 },
];

function SearchUsers(props) {
  const classes = useStyles();
  const [allProfiles, setAllProfiles] = useState([]);
  const [allClearanceLevels, setAllClearanceLevels] = useState([]);
  const [profile, setProfile] = useState(null);
  const [clearance, setClearance] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [allUsersData, setAllUsersData] = useState([]);
  const [selection, setSelection] = useState(null);
  const [locked, setLocked] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [userName, setUsername] = useState('');
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchProfiles();
    fetchClearanceLevels();
  }, []);

  useEffect(() => {
    let filteredUsers = allUsersData;

    if (allUsersData) {
      if (profile) {
        filteredUsers = filteredUsers.filter(u => u.ptsProfileID === profile.ptsProfileID);
      }
      if (clearance) {
        filteredUsers = filteredUsers.filter(
          u => u.ptsClearanceLevelID === clearance.ptsClearanceLevelID
        );
      }

      if (userName) {
        filteredUsers = filteredUsers.filter(u =>
          u.Username.toLowerCase().includes(userName.toLowerCase())
        );
      }
      if (fullName) {
        filteredUsers = filteredUsers.filter(u =>
          u.FullName.toLowerCase().includes(fullName.toLowerCase())
        );
      }
      setUsersData(filteredUsers);
    }
  }, [profile, clearance, userName, fullName, allUsersData]);

  const fetchUsers = async () => {
    const result = await findSearchUsers();
    setAllUsersData(processData(result));
  };

  const processData = data => {
    return data.map(row => {
      return {
        ...row,
        id: row.ptsUserID,
      };
    });
  };

  const fetchProfiles = async () => {
    const result = await findSearchProfiles();
    setAllProfiles(result);
  };

  const fetchClearanceLevels = async () => {
    const result = await findSearchClearanceLevels();
    setAllClearanceLevels(result);
  };

  const handleSaveClick = async () => {
    const data = {
      ProfileId: profile.ptsProfileID,
      ClearanceId: clearance ? clearance.ptsClearanceLevelID : null,
      IsLockedOut: locked ? 1 : 0,
      UserId: selection,
    };

    try {
      await updateSearchUser(selection, data);
      fetchUsers();
      props.notify('User Updated', 'success');
    } catch (err) {
      props.handleError(err, 'Error Updating User');
    }
  };

  const handleSelectChange = newSelection => {
    const currentUserId = parseInt(newSelection.selectionModel[0], 10);
    const currentUser = usersData.find(data => data.ptsUserID === currentUserId);
    setSelectedUser(currentUser);
    setSelection(currentUserId);
  };

  const renderSave = () => {
    return (
      <div className={classes.saveBtn}>
        <Button
          onClick={handleSaveClick}
          disabled={!profile || !selection}
          variant="contained"
          color="primary">
          <SaveIcon className="mr-2" /> Save
        </Button>
      </div>
    );
  };

  const renderUsername = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <TextField
          label="Username"
          variant="outlined"
          value={userName}
          size="small"
          onChange={e => setUsername(e.target.value)}
          fullWidth
          className={classes.textField}
        />
      </Box>
    );
  };

  const renderFullName = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <TextField
          label="Full Name"
          variant="outlined"
          value={fullName}
          size="small"
          onChange={e => setFullName(e.target.value)}
          fullWidth
          className={classes.textField}
        />
      </Box>
    );
  };

  const renderProfiles = () => {
    const handleChange = (e, newValue) => setProfile(newValue);
    return (
      <Box style={{ marginLeft: 10 }}>
        <Autocomplete
          disablePortal
          options={allProfiles}
          className={classes.autoComplete}
          renderInput={params => <TextField {...params} label="Profiles" variant="outlined" />}
          onChange={handleChange}
          getOptionLabel={option => option.Label}
          renderOption={option => `${option.Label} ${option.Type ? -option.Type : ''}`}
          size="small"
          value={profile}
        />
      </Box>
    );
  };

  const renderClearance = () => {
    const handleChange = (e, newValue) => setClearance(newValue);
    return (
      <Box style={{ marginLeft: 10 }}>
        <Autocomplete
          disablePortal
          options={allClearanceLevels}
          className={classes.autoComplete}
          renderInput={params => (
            <TextField {...params} label="Clearance Levels" variant="outlined" />
          )}
          onChange={handleChange}
          getOptionLabel={option => option.Level}
          size="small"
          value={clearance}
        />
      </Box>
    );
  };

  const renderUsers = () => {
    return (
      <div className={classes.list}>
        <XGrid
          columns={columns}
          rows={usersData}
          rowHeight={38}
          onSelectionModelChange={handleSelectChange}
          disableMultipleSelection
          hideFooter
        />
      </div>
    );
  };

  const renderActions = () => {
    return (
      <div className={classes.saveBtn}>
        <Tooltip
          title="Add"
          onClick={() =>
            props.showAddSecUser({ fetchUsers: fetchUsers, setSelection: setSelection })
          }>
          <Fab size="small" color="secondary">
            <AddIcon />
          </Fab>
        </Tooltip>
        <Tooltip
          title="Edit"
          onClick={() =>
            props.showAddSecUser({
              selectedUser: selectedUser,
              fetchUsers: fetchUsers,
              setSelection: setSelection,
            })
          }
          disabled={!selection}>
          <Fab size="small" color="primary" style={{ marginLeft: 10 }}>
            <EditIcon />
          </Fab>
        </Tooltip>
        <Tooltip
          title="Delete User"
          disabled={!selection}
          onClick={() => {
            props.showRemoveSecUser({
              selectedUser: selection,
              fetchUsers: fetchUsers,
              setSelection: setSelection,
              userName: selectedUser.Username,
            });
          }}>
          <Fab size="small" style={{ marginLeft: 10 }}>
            <DeleteIcon style={{ color: selection ? 'red' : '' }} />
          </Fab>
        </Tooltip>
      </div>
    );
  };

  return (
    <Content>
      <div className="m-4">
        <h4 className="mb-2">Search Users</h4>
        <Grid container item xs={12} style={{ marginTop: 10, marginBottom: 15 }}>
          <Grid item xs={3}>
            {renderUsername()}
          </Grid>
          <Grid item xs={3}>
            {renderFullName()}
          </Grid>
          <Grid item xs={3}>
            {renderProfiles()}
          </Grid>
          <Grid item xs={3}>
            {renderClearance()}
          </Grid>
        </Grid>
        {renderActions()}
        {renderUsers()}
      </div>
    </Content>
  );
}

export default connect(null, { handleError, notify, showAddSecUser, showRemoveSecUser })(
  SearchUsers
);
